import { useState, useRef } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { AudioLibraryStep } from './AudioLibraryStep'
import { AudioTimelineStep } from './AudioTimelineStep'
import type { MediaObject } from 'types'
import { Button } from 'blaze-ui/Button/Button'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useSaveDesignAudio } from 'hooks/queries/designs/useSaveDesignAudio'
import { useAudioEditorState } from './hooks/useAudioEditorState'
import { useVideoTransformChannel } from '../hooks/useVideoTransformChannel'
import Styles from './AudioEditorModal.module.scss'
import { ConfirmModal } from 'blaze-components/ConfirmModal/ConfirmModal'

interface AudioEditorModalProps {
  mediaObject: MediaObject
  almanacFileKey: string
  onAudioTransformComplete: () => void
}

export const AudioEditorModal = ({ mediaObject, almanacFileKey, onAudioTransformComplete }: AudioEditorModalProps) => {
  const { closeModal, openModal } = useModals()
  const [isProcessing, setIsProcessing] = useState(false)
  const dismissToastRef = useRef<(() => void) | null>(null)

  const {
    hasOriginalAudio,
    hasExistingMusicTrack,
    hasNativeAudioConfig,
    hasChanges,
    step,
    selectedAudio,
    musicStartTime,
    isVideoMuted,
    setMusicStartTime,
    handleSelectAudio,
    handleReplaceAudio,
    handleClearAudio,
    handleToggleVideoMute,
  } = useAudioEditorState(mediaObject)

  useVideoTransformChannel({
    almanacFileKey,
    onAudioSaveComplete: () => {
      dismissToastRef.current?.()
      showToastMessage('Audio saved successfully', 'success')
      onAudioTransformComplete()
      closeModal()
    },
    onAudioSaveFailed: (error) => {
      dismissToastRef.current?.()
      showToastMessage(error || 'Failed to save audio', 'error')
      setIsProcessing(false)
    },
  })

  const saveAudioMutation = useSaveDesignAudio({
    onSuccess: (data) => {
      if (data.processing) {
        setIsProcessing(true)
      } else {
        dismissToastRef.current?.()
        showToastMessage('Audio saved successfully', 'success')
        onAudioTransformComplete()
        closeModal()
      }
    },
    onError: () => {
      dismissToastRef.current?.()
      showToastMessage('Failed to save audio', 'error')
    },
  })

  const isLoading = saveAudioMutation.isLoading || isProcessing

  const handleSave = () => {
    dismissToastRef.current = showToastMessage('Processing audio...', 'loading', undefined, 0)

    saveAudioMutation.mutate({
      almanacFileKey,
      selectedAudioId: selectedAudio?.id ?? null,
      selectedAudioTitle: selectedAudio?.title ?? null,
      selectedAudioDuration: selectedAudio?.duration ?? null,
      musicStartTime,
      removeOriginalAudio: isVideoMuted,
      audioSource: selectedAudio?.type === 'brand_kit' ? 'brand_kit' : 'stock',
      audioMediaFileKey: selectedAudio?.media_file_key ?? null,
    })
  }

  const handleCloseTimeline = () => {
    if (!hasChanges) {
      closeModal()
      return
    }

    openModal(ConfirmModal, {
      title: 'Unsaved Changes',
      prompt: 'You have unsaved changes. Are you sure you want to leave?',
      cancelText: 'Cancel',
      confirmText: 'Leave',
      onConfirm: closeModal,
    })
  }

  const isLibrary = step === 'library'

  return (
    <Modal.Root size={isLibrary ? 'md' : 'lg'} onPressOutside={() => {}}>
      <Modal.Header
        className={isLibrary ? undefined : Styles.centeredHeader}
        title={isLibrary ? 'Select audio' : 'Edit audio'}
        headingLevel={2}
        onClose={isLibrary ? closeModal : handleCloseTimeline}
      />
      <Modal.Content withoutFooter className={isLibrary ? undefined : Styles.timelineContent}>
        {isLibrary ? (
          <AudioLibraryStep onSelectAudio={handleSelectAudio} minDuration={mediaObject.duration ?? 0} />
        ) : (
          <AudioTimelineStep
            mediaObject={mediaObject}
            selectedAudio={selectedAudio}
            musicStartTime={musicStartTime}
            isVideoMuted={isVideoMuted}
            hasOriginalAudio={hasOriginalAudio}
            hasExistingMusicTrack={hasExistingMusicTrack}
            hasNativeAudioConfig={hasNativeAudioConfig}
            onMusicStartTimeChange={setMusicStartTime}
            onToggleVideoMute={handleToggleVideoMute}
            onReplaceAudio={handleReplaceAudio}
            onRemoveAudio={handleClearAudio}
          />
        )}
      </Modal.Content>
      {!isLibrary && (
        <Modal.Footer>
          <Modal.FooterContent slot="right">
            <Button size="lg" onPress={handleSave} isDisabled={isLoading || !hasChanges}>
              Save
            </Button>
          </Modal.FooterContent>
        </Modal.Footer>
      )}
    </Modal.Root>
  )
}
